import { useState } from 'react';
import { toWireDate, type DealDetail } from '@forecastify/shared';
import { Button, Icon } from '@forecastify/ui';
import { useI18n, useT, type MessageKey } from '../../i18n';
import { useSession } from '../../session';
import { useWorkspaceNavigate } from '../../workspace-path';
import { Attachments } from '../attachments';
import { SectionHeader } from '../contracts/detail-chrome';
import { DOCUMENT_FILES } from '../file-kinds';
import { formatCalendarDate } from '../history/history-labels';
import { ActivityFeed, eventIcon } from './activity-feed';
import { dealMoney, eventKindLabel } from './deal-kit';
import type { DealTabProps } from './deal-tab';
import { NextStepDialog } from './next-step-dialog';
import { OverviewComposer } from './overview-composer';
import { PersonAvatar, Refusal, dueDateOf, useCardWrite } from './overview-parts';
import { OverviewRail } from './overview-rail';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D5 · the record's *Áttekintés*.                   │
 * │ `phase6/06-deal-overview.png`, `06b`–`06d`, `13`.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Left: the closed deal's banner, the composer, the next step, the open to-dos, the files and the
 * feed. Right: the rail (`overview-rail.tsx`). Two columns from `lg`, one below it in the same
 * reading order (`state-rules.md` §9).
 *
 * ⚠️ Every write goes through `record.write`, whose answer IS the record — so the feed, the to-do
 * list and the rail all move from the one response. A reader who cannot manage the deal sees the
 * same page with no controls on it; the server refuses regardless.
 */
export function DealOverviewTab({ deal, record, feeds, canManage, onTab }: DealTabProps) {
  const session = useSession();
  const [editingNext, setEditingNext] = useState(false);
  const composerWrite = useCardWrite(record);
  const feedWrite = useCardWrite(record);
  const me =
    session.workspaces.find((workspace) => workspace.id === session.workspaceId)?.memberId ?? '';

  return (
    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 p-5 lg:grid-cols-[minmax(380px,1fr)_308px]">
      <div className="flex min-w-0 flex-col gap-5">
        <ClosedBanner deal={deal} />
        {canManage ? (
          <OverviewComposer
            members={feeds.members}
            defaultAssigneeId={me}
            write={composerWrite}
            onNextStep={() => {
              setEditingNext(true);
            }}
          />
        ) : null}
        <NextStepCard
          deal={deal}
          record={record}
          feeds={feeds}
          canManage={canManage}
          onEdit={() => {
            setEditingNext(true);
          }}
        />
        <TodosCard deal={deal} record={record} feeds={feeds} canManage={canManage} />
        <DealFiles deal={deal} canManage={canManage} />
        <ActivityFeed deal={deal} canManage={canManage} write={feedWrite} />
        <Refusal error={feedWrite.error} />
      </div>
      <OverviewRail deal={deal} record={record} feeds={feeds} canManage={canManage} onTab={onTab} />
      <NextStepDialog
        open={editingNext}
        deal={deal}
        record={record}
        feeds={feeds}
        onClose={() => {
          setEditingNext(false);
        }}
      />
    </div>
  );
}

/* ── the closed deal's banner ──────────────────────────────────────────────────────────────── */

/**
 * *Megnyert · 2026. aug 1. · 9 600 000 Ft · Referencia és bizalom* over the close note, and what
 * the generation made from it — or *Elvesztett · date · reason · competitor*.
 */
function ClosedBanner({ deal }: { readonly deal: DealDetail }) {
  const t = useT();
  const { locale } = useI18n();
  const go = useWorkspaceNavigate();
  if (deal.statusCategory === 'open') return null;
  const won = deal.statusCategory === 'won';
  const date = deal.closedOn === null ? '' : formatCalendarDate(deal.closedOn, locale);
  const headline = (
    won
      ? [
          t('dealOverview.banner.won'),
          date,
          dealMoney(deal.wonValue ?? deal.value, deal.currencyCode),
          deal.reasonName ?? '',
        ]
      : [t('dealOverview.banner.lost'), date, deal.reasonName ?? '', deal.competitor]
  )
    .filter((part) => part !== '')
    .join(' · ');

  return (
    <div
      className={
        won
          ? 'flex items-start gap-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3'
          : 'border-destructive/40 bg-destructive/5 flex items-start gap-2.5 rounded-lg border p-3'
      }
    >
      <Icon
        name={won ? 'circle-check' : 'x'}
        size={14}
        className={won ? 'mt-0.5 text-emerald-600' : 'text-destructive mt-0.5'}
      />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-medium tabular-nums">{headline}</div>
        {deal.closeNote === '' ? null : (
          <div className="text-muted-foreground mt-0.5 text-[11.5px] leading-relaxed whitespace-pre-line">
            {deal.closeNote}
          </div>
        )}
        {won && deal.conversions.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px]">
            <span className="text-muted-foreground">{t('dealOverview.banner.converted')}</span>
            {deal.conversions.flatMap((conversion) => [
              conversion.projectId === null ? null : (
                <Button
                  key={`p-${conversion.projectId}-${conversion.createdAt}`}
                  variant="link"
                  size="sm"
                  className="h-auto px-0 text-[11.5px]"
                  onClick={() => {
                    go(`/projects/${conversion.projectId ?? ''}`);
                  }}
                >
                  {t('dealOverview.banner.project', { name: conversion.projectName ?? '' })}
                </Button>
              ),
              conversion.contractIdentifier === null ? null : (
                <Button
                  key={`c-${conversion.contractIdentifier}-${conversion.createdAt}`}
                  variant="link"
                  size="sm"
                  className="h-auto px-0 text-[11.5px]"
                  onClick={() => {
                    go(`/contracts/${conversion.contractIdentifier ?? ''}`);
                  }}
                >
                  {t('dealOverview.banner.contract', {
                    name: conversion.contractName ?? conversion.contractIdentifier,
                  })}
                </Button>
              ),
            ])}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ── the next step ─────────────────────────────────────────────────────────────────────────── */

function NextStepCard({
  deal,
  record,
  feeds,
  canManage,
  onEdit,
}: Pick<DealTabProps, 'deal' | 'record' | 'feeds' | 'canManage'> & {
  readonly onEdit: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const write = useCardWrite(record);
  const step = deal.nextStep;

  return (
    <div className="flex flex-col gap-1.5">
      <SectionHeader title={t('dealOverview.next.title')} />
      {step === null ? (
        <div className="text-muted-foreground rounded-lg border border-dashed px-3 py-3 text-[11.5px]">
          {canManage ? t('dealOverview.next.empty') : t('dealOverview.next.emptyReadOnly')}
        </div>
      ) : (
        <div className="bg-card flex flex-wrap items-center gap-2.5 rounded-lg border px-3 py-2.5">
          {canManage ? (
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={write.busy}
              aria-label={t('dealOverview.next.done')}
              onClick={() => void write.run('/next-step/done', 'POST')}
            >
              <Icon name="circle" size={14} />
            </Button>
          ) : null}
          <span className="min-w-0 flex-1 text-[12.5px] leading-snug">{step.text}</span>
          <span className="flex shrink-0 items-center gap-1.5 text-[11.5px]">
            <PersonAvatar
              name={step.memberName ?? t('dealOverview.next.unassigned')}
              memberId={step.memberId}
              members={feeds.members}
            />
            <span className="text-muted-foreground max-w-[140px] truncate">
              {step.memberName ?? t('dealOverview.next.unassigned')}
            </span>
          </span>
          {step.due === null ? null : (
            <span
              className={`shrink-0 text-[11px] tabular-nums ${deal.nextStepLate ? 'text-destructive' : 'text-muted-foreground'}`}
              {...(deal.nextStepLate ? { title: t('deals.next.late') } : {})}
            >
              {formatCalendarDate(step.due, locale)}
            </span>
          )}
          {canManage ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground shrink-0"
              aria-label={t('dealOverview.next.edit')}
              onClick={onEdit}
            >
              <Icon name="pencil" size={12} />
            </Button>
          ) : null}
        </div>
      )}
      <Refusal error={write.error} />
    </div>
  );
}

/* ── the open to-dos ───────────────────────────────────────────────────────────────────────── */

function TodosCard({
  deal,
  record,
  feeds,
  canManage,
}: Pick<DealTabProps, 'deal' | 'record' | 'feeds' | 'canManage'>) {
  const t = useT();
  const { locale } = useI18n();
  const write = useCardWrite(record);
  const today = toWireDate(new Date());
  const open = deal.events.filter(
    (event) => (event.kind === 'task' || event.kind === 'meeting') && !event.done,
  );

  return (
    <div className="flex flex-col gap-1.5">
      <SectionHeader title={t('dealOverview.todos.title')} />
      {open.length === 0 ? (
        <div className="text-muted-foreground rounded-lg border border-dashed px-3 py-3 text-[11.5px]">
          {t('dealOverview.todos.empty')}
        </div>
      ) : (
        <ul className="bg-card overflow-hidden rounded-lg border">
          {open.map((event) => {
            const title = event.title === '' ? eventKindLabel(t, event.kind) : event.title;
            const due = event.dueAt === null ? null : dueDateOf(event.dueAt);
            return (
              <li
                key={event.id}
                className="flex items-center gap-2.5 border-b px-3 py-2 last:border-0"
              >
                {canManage ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={write.busy}
                    aria-label={t('dealOverview.todos.done', { title })}
                    onClick={() =>
                      void write.run(`/events/${event.id}`, 'PATCH', {
                        version: event.version,
                        done: true,
                      })
                    }
                  >
                    <Icon name="circle" size={14} />
                  </Button>
                ) : null}
                <Icon
                  name={eventIcon(event.kind)}
                  size={13}
                  className="text-muted-foreground shrink-0"
                />
                <span className="min-w-0 flex-1 truncate text-[12.5px]">{title}</span>
                {/* 30 Oct 2026 · both slots drawn even when empty, at fixed widths, so the columns
                    line up row to row (UAT). */}
                <span className="hidden w-[140px] shrink-0 items-center gap-1.5 text-[11.5px] sm:flex">
                  {event.assigneeName === null ? null : (
                    <>
                      <PersonAvatar
                        name={event.assigneeName}
                        memberId={event.assigneeMemberId}
                        members={feeds.members}
                      />
                      <span className="text-muted-foreground min-w-0 truncate">
                        {event.assigneeName}
                      </span>
                    </>
                  )}
                </span>
                <span
                  className={`w-[88px] shrink-0 text-right text-[11px] tabular-nums ${due !== null && due < today ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  {due === null ? null : formatCalendarDate(due, locale)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <Refusal error={write.error} />
    </div>
  );
}

/* ── the files ─────────────────────────────────────────────────────────────────────────────── */

/** ⚠️ The four upload codes C22 made distinct, each with its own sentence — rule 5. */
const ATTACH_ERROR: Readonly<Record<string, MessageKey>> = {
  file_too_large: 'task.attachment.tooLarge',
  file_infected: 'task.attachment.infected',
  scanner_unavailable: 'task.attachment.scannerDown',
  unsupported_file_type: 'task.attachment.failed',
};

/**
 * The deal's folder — the same `Attachments` list a contract mounts, on file target `deal`. The
 * list reloads itself after an upload resolves, so nothing up here holds a copy of it.
 */
function DealFiles({
  deal,
  canManage,
}: {
  readonly deal: DealDetail;
  readonly canManage: boolean;
}) {
  const t = useT();
  const session = useSession();
  const [failure, setFailure] = useState<string | null>(null);

  const upload = async (file: File): Promise<void> => {
    setFailure(null);
    const form = new FormData();
    form.set('file', file);
    const response = await session
      .request(`/files?target=deal&targetId=${deal.id}`, { method: 'POST', body: form })
      .catch(() => null);
    if (response?.ok === true) return;
    const envelope = (await response?.json().catch(() => null)) as
      { error?: { code?: string } } | null | undefined;
    setFailure(envelope?.error?.code ?? 'unavailable');
  };

  return (
    <section>
      {failure === null ? null : (
        <p className="text-destructive mb-1 text-[11.5px]" role="alert">
          {t(ATTACH_ERROR[failure] ?? 'task.attachment.failed')}
        </p>
      )}
      <Attachments
        target="deal"
        targetId={deal.id}
        canEdit={canManage}
        heading={t('dealOverview.files.title')}
        variant="section"
        emptyNote={t('dealOverview.files.empty')}
        accept={DOCUMENT_FILES}
        onUpload={async (files) => {
          for (const file of files) await upload(file);
        }}
      />
    </section>
  );
}
